import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../constants';
import { FormGenerator } from '../../helper/FormGenerator';
import { useTokens } from '../../context/TokenContext';
import { countryOptions, stateOptions } from '../../utils/appUtils';

const EditProfileVendor = ({ vendorData, onReload }) => {
    const { getItem } = useTokens();
    const authToken = getItem('vendor', 'AuthToken');  // Use token from context
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({});
    const [userData, setUserData] = useState(null);

    const isRestricted = vendorData && (vendorData.status === '1' || vendorData.status === 1) && Array.isArray(vendorData.roles) && vendorData.roles.includes('ROLE_VENDOR');

    useEffect(() => {
        if (vendorData) {
            // Map API response to form fields
            const mappedData = {
                first_name: vendorData.first_name || '',
                last_name: vendorData.last_name || '',
                store_name: vendorData.store_name || '',
                store_legal_name: vendorData.store_legal_name || '',
                phone: vendorData.phone || '',
                email: vendorData.email || '',
                address: vendorData.address || '',
                city: vendorData.city || '',
                zip: vendorData.zip || '',
                state: vendorData.state || '',
                country: vendorData.country || '',
                gst: vendorData.gst || '',
                fssai_number: vendorData.fssai_number || '',
                profile_picture: vendorData.profile_picture || '',
                brand_logo: vendorData.documents?.brand_logo || '',
                file_aadhar_card: vendorData.documents?.file_aadhar_card || '',
                file_pan_card: vendorData.documents?.file_pan_card || '',
                file_fssai_certificate: vendorData.documents?.file_fssai_certificate || '',
                file_bank_passbook: vendorData.documents?.file_bank_passbook || '',
                extraDocuments: vendorData.extraDocuments || []
            };
            setFormData(mappedData);
            setUserData(vendorData);
        }
    }, [vendorData]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const toastId = toast.loading('Saving your settings...', { autoClose: false });

        // Validate required fields
        if (!formData.first_name || !formData.last_name || !formData.phone || !formData.email || !formData.store_name || !formData.store_legal_name) {
            toast.update(toastId, {
                render: 'Please fill out all required fields.',
                type: 'error',
                isLoading: false,
                autoClose: 3000,
            });
            setLoading(false);
            return;
        }

        try {
            const response = await axios.put(`${API_URL}/vendor/update`, formData, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
            });

            if (response.status === 200) {
                toast.update(toastId, {
                    render: 'Settings saved successfully!',
                    type: 'success',
                    isLoading: false,
                    autoClose: 3000,
                });
                setUserData(formData); // Update user data
                if (onReload) onReload(); // Reload parent data
            }
        } catch (error) {
            toast.update(toastId, {
                render: 'Failed to save settings. Please try again.',
                type: 'error',
                isLoading: false,
                autoClose: 3000,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    return (
        loading ? (
            <div className="loading-spinner text-center">
                <span className="spinner-border spinner-border-sm me-3" role="status" aria-hidden="true"></span>
                Loading...
            </div>
        ) : (
            <div>
                <h2>Profile</h2>
                <p>Update your profile information below.</p>

                {/* Bootstrap Form */}
                <form onSubmit={handleFormSubmit}>

                    <div className='row'>
                        {/* Profile Picture Input using FormGenerator */}
                        <FormGenerator
                            fields={[
                                { id: 'first_name', label: 'Owner First Name', type: 'text', class: 'col-md-6', required: true, disabled: isRestricted },
                                { id: 'last_name', label: 'Owner Last Name', type: 'text', class: 'col-md-6', required: true, disabled: isRestricted },
                                { id: 'store_name', label: 'Store Display Name', type: 'text', class: 'col-md-6', required: true, disabled: isRestricted ? false : false },
                                { id: 'store_legal_name', label: 'Store Legal Name', type: 'text', class: 'col-md-6', required: true, disabled: isRestricted },
                                { id: 'gst', label: 'GSTIN Number', type: 'text', class: 'col-md-6', required: true, disabled: isRestricted },
                                { id: 'fssai_number', label: 'FSSAI Number', type: 'text', class: 'col-md-6', required: true, disabled: isRestricted },
                                { id: 'phone', label: 'Phone Number', type: 'tel', class: 'col-md-6', required: true, prefix: '+91', minLength: 10, maxLength: 10, isNumeric: true, disabled: isRestricted },
                                { id: 'email', label: 'Email', type: 'email', class: 'col-md-6', required: true, disabled: isRestricted },
                                { id: 'address', label: 'Address', type: 'textarea', class: 'col-md-12', required: true, disabled: isRestricted },
                                { id: 'city', label: 'City', type: 'text', class: 'col-md-6', required: true, disabled: isRestricted },
                                { id: 'zip', label: 'PIN Code', type: 'text', class: 'col-md-6', required: true, minLength: 6, maxLength: 6, isNumeric: true, disabled: isRestricted },
                                { id: 'state', label: 'State', type: 'select', options: stateOptions, searchable: true, class: 'col-md-6', required: true, disabled: isRestricted },
                                { id: 'country', label: 'Country', type: 'select', options: countryOptions, class: 'col-md-6', required: true, disabled: isRestricted },
                                { id: 'profile_picture', label: 'Profile Picture', type: 'upload_single', class: 'col-md-6', required: true, disabled: isRestricted },
                            ]}
                            fieldData={formData}
                            onChange={(field, value) => handleInputChange({ target: { name: field, value } })}
                            hTure={false}
                            authToken={authToken}
                        />
                    </div>

                    {/* Save Settings Button */}
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? 'Saving...' : 'Save Settings'}
                    </button>
                </form>
            </div>
        )
    );
};

export default EditProfileVendor;
